import api, { ApiResponse } from './api';

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  expiredUsers: number;
  expiringSoon: number;
  liveEvents: number;
  systemStats: {
    daznConfigured: number;
    proxyConfigured: number;
    xaccelConfigured: number;
    setupCompleted: number;
  };
  eventStats: {
    total: number;
    scheduled: number;
    live: number;
    completed: number;
    errors: number;
  };
  userList: Array<{
    id: number;
    username: string;
    email: string;
    is_active: number;
    expires_at: string | null;
    last_login_at: string | null;
    setup_completed: number;
    created_at: string;
    live_events: number;
  }>;
}

export interface UserListItem {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user';
  is_active: number;
  expires_at: string | null;
  package_id: number | null;
  package_name: string | null;
  language: string;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export interface UserDetail extends UserListItem {
  stats: {
    proxies: number;
    xaccelServers: number;
    sports: number;
    competitions: number;
    scheduledEvents: number;
    liveEvents: number;
  };
}

export interface AdminUserDetail {
  user: {
    id: number; username: string; email: string; is_active: number;
    expires_at: string | null; created_at: string; last_login_at: string | null;
    setup_completed: number; language: string;
  };
  daznAccount: {
    dazn_email: string | null; dazn_password: string | null; dazn_device_id: string | null;
    dazn_country: string | null; dazn_language: string | null;
    dazn_timezone: string | null; dazn_user_agent: string | null; dazn_x_dazn_ua: string | null;
    dazn_pin: string | null; dazn_user_id: string | null;
    dazn_first_name: string | null; dazn_last_name: string | null;
    subscription_status: string | null; subscription_plan: string | null;
    subscription_expires_at: string | null; last_login_at: string | null;
    tokenStatus: { hasToken: boolean; expiresAt: string | null };
  } | null;
  proxies: Array<{
    id: number; name: string; proxy_host: string; proxy_port: number;
    proxy_type: string; proxy_username: string | null; proxy_password: string | null;
    is_active: number; is_default: number;
    last_check_at: string | null; last_check_status: string | null;
  }>;
  xaccelServers: Array<{
    id: number; name: string; server_url: string; server_token: string;
    is_active: number; is_default: number; created_at: string;
  }>;
  sports: Array<{
    id: number; name: string; dazn_asset_id: string; sort_order: number;
    competitions: Array<{
      id: number; name: string; dazn_asset_id: string; channel_mode: string;
      num_channels: number; channel_prefix: string; is_active: number;
      xaccel_server_name: string | null; resolutions: string; teams_count: number;
    }>;
  }>;
  schedule: {
    today: Array<{
      id: number; event_title: string; event_start_time: string; event_end_time: string;
      status: number; xaccel_channel_name: string | null; competition_name: string | null;
    }>;
    stats: { scheduled: number; live: number; completed: number; errors: number };
  };
  linearChannels: Array<{
    id: number; name: string; status: string;
    auto_restart_enabled: number; xaccel_channel_name: string | null;
  }>;
  resolutions: Array<{
    id: number; name: string; xaccel_value: string; sort_order: number;
  }>;
  counts: {
    proxies: number; xaccelServers: number; sports: number; competitions: number;
    scheduledToday: number; liveNow: number; linearChannels: number; resolutions: number;
  };
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  expires_at?: string;
  package_id?: number;
  language?: string;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  password?: string;
  expires_at?: string;
  package_id?: number;
  language?: string;
  is_active?: boolean;
}

export interface UsersListResponse {
  users: UserListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UserDiagnostics {
  userId: number;
  username: string;
  isActive: boolean;
  dazn: { status: 'ok' | 'expired' | 'missing'; tokenExpiresInMin: number | null };
  identity?: { androidtvChannels: number; rejectedSince: string | null; uaMismatches: string[]; droppingLegs: string[]; rolloutOffWithRows?: { rows: number; channels: number } | null };
  /** Streaming servers of the user that do not answer (circuit open): skipped by background work for 5 min at a time. */
  serversCircuitOpen?: Array<{ serverId: number; name: string; since: string; retryAt: string; reason: string }>;
  events: {
    total48h: number;
    completed: number;
    live: number;
    failedCount: number;
    failures: Array<{
      category: string;
      label: string;
      action: string;
      severity: 'error' | 'warning' | 'info';
      count: number;
      samples: Array<{ title: string; time: string; message: string }>;
    }>;
  };
  linear: {
    total: number;
    errors: string[];
    /** Channels with held legs (stopped by the user / no longer selected): nothing in the background starts them. */
    heldLegs?: Array<{ channelId: number; channel: string; legs: Array<{ name: string; serverId: number; reason: 'user-stop' | 'unwanted'; status: string }> }>;
  };
  paywallUpcoming: { ppv: number; extra: number };
  level: 'ok' | 'warning' | 'error';
}

export const usersService = {
  async getAdminStats(): Promise<AdminStats> {
    const response = await api.get<ApiResponse<AdminStats>>('/users/stats');
    return response.data.data;
  },

  async getDiagnostics(): Promise<UserDiagnostics[]> {
    const response = await api.get<ApiResponse<UserDiagnostics[]>>('/users/diagnostics');
    return response.data.data;
  },

  async list(params?: { search?: string; page?: number; limit?: number }): Promise<UsersListResponse> {
    const response = await api.get<ApiResponse<UsersListResponse>>('/users', { params });
    return response.data.data;
  },

  async get(id: number): Promise<{ user: UserDetail; stats: UserDetail['stats'] }> {
    const response = await api.get<ApiResponse<{ user: UserDetail; stats: UserDetail['stats'] }>>(`/users/${id}`);
    return response.data.data;
  },

  async create(data: CreateUserRequest): Promise<UserListItem> {
    const response = await api.post<ApiResponse<UserListItem>>('/users', data);
    return response.data.data;
  },

  async update(id: number, data: UpdateUserRequest): Promise<UserListItem> {
    const response = await api.put<ApiResponse<UserListItem>>(`/users/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async toggleActive(id: number): Promise<{ is_active: boolean }> {
    const response = await api.post<ApiResponse<{ is_active: boolean }>>(`/users/${id}/toggle-active`);
    return response.data.data;
  },

  async extendSubscription(id: number, data: { days?: number; new_expires_at?: string }): Promise<{ expires_at: string }> {
    const response = await api.post<ApiResponse<{ expires_at: string }>>(`/users/${id}/extend`, data);
    return response.data.data;
  },

  async getUserDetail(id: number): Promise<AdminUserDetail> {
    const response = await api.get<ApiResponse<AdminUserDetail>>(`/users/${id}/detail`);
    return response.data.data;
  },
};
